/**
 * Client-side mirrors of the server's trade guards — the fast path, not the
 * authority. The server re-runs all of them (e.g. the DTO pattern alone accepts `"0"`, which only `validateQuantity` rejects).
 */

import { parseISO, startOfDay } from "date-fns";

import { isAfterLocalToday } from "../../../utils";
import { isBlank } from "../../../utils/taxWithheld";
import { ChargeNoun, NumericInput } from "../types";
import {
  CashbackFundingFormValues,
  ChargeFormValues,
  TaxedIncome,
} from "./types";

/** DECIMAL(28,6) — mirrors `QUANTITY_PATTERN` in the holdings DTO. */
const QUANTITY_PATTERN = /^\d+(\.\d{1,6})?$/;

/** `MAX_QUANTITY_INTEGER_DIGITS` in `holdingsTradeService.ts`. */
const MAX_QUANTITY_INTEGER_DIGITS = 22;

// A comma decimal separator is accepted so a pt-PT "0,027055" isn't rejected; the API only takes a dot.
export const normalizeQuantity = (raw: string | undefined): string =>
  (raw ?? "").trim().replace(",", ".");

// Validated as a string throughout — `Number("1.0000001")` already loses the precision this checks.
export const validateQuantity = (raw: string | undefined): string | true => {
  const value = normalizeQuantity(raw);
  if (!value) {
    return "Quantity is required!";
  }
  if (!QUANTITY_PATTERN.test(value)) {
    return "Quantity must be a positive number with at most 6 decimal places";
  }
  // Every digit being a zero is the only way a pattern-valid quantity can be
  // zero, whatever the scale ("0", "0.000000").
  if (!/[1-9]/.test(value)) {
    return "Quantity must be greater than 0";
  }
  const [integerPart] = value.split(".");
  if (integerPart.replace(/^0+/, "").length > MAX_QUANTITY_INTEGER_DIGITS) {
    return "Quantity is too large";
  }
  return true;
};

// Exponent notation can't be whole cents, so it's reported as over-precise rather than parsed.
const fractionalDigits = (value: number): number => {
  const text = String(value);
  if (text.includes("e") || text.includes("E")) {
    return Number.POSITIVE_INFINITY;
  }
  const dotIndex = text.indexOf(".");
  return dotIndex === -1 ? 0 : text.length - dotIndex - 1;
};

// `toCents` 400s on anything not representable as whole cents, and `FormMoneyInput` accepts negatives/arbitrary precision, so both are checked here.
export const validateAmount = (value: NumericInput): string | true => {
  if (value === "" || value === undefined || value === null) {
    return "Amount is required!";
  }
  if (!Number.isFinite(value) || value <= 0) {
    return "Amount must be greater than 0";
  }
  if (fractionalDigits(value) > 2) {
    return "Amount must have at most 2 decimal places";
  }
  return true;
};

/**
 * The earliest date a trade may carry — the investment account's own start
 * date (single, since PMA-318 moved money inside the broker). Parsed as a calendar date, not `new Date()`, which is UTC and drifts a day west of Greenwich.
 */
export const tradeDateFloor = (investmentStartDate: string): Date =>
  parseISO(investmentStartDate);

// `floorAccountName` only changes the wording, never the verdict — a brokerage
// transfer has two start dates, so naming the later one avoids leaving the user to guess which.
export const validateTradeDate = (
  value: Date,
  minDate: Date,
  floorAccountName?: string,
): string | true => {
  if (!value) {
    return "Date is required!";
  }
  if (isAfterLocalToday(value)) {
    return "Date cannot be in the future";
  }
  const selectedDay = startOfDay(value);
  // Compare against a copy — `setHours` mutates, and `minDate` is a memo that
  // other renders read.
  const floor = new Date(minDate);
  floor.setHours(0, 0, 0, 0);
  if (selectedDay.getTime() >= floor.getTime()) {
    return true;
  }
  return floorAccountName
    ? `Transaction Date should be after the ${floorAccountName} account start date`
    : "Date must be on or after the account's start date";
};

/** Optional — but `@Min(0.01)` means a present charge still has to be a real one. */
const validateCharge =
  (noun: string) =>
  (value: NumericInput): string | true => {
    if (value === "" || value === undefined || value === null) {
      return true;
    }
    if (!Number.isFinite(value) || value <= 0) {
      return `${noun} must be greater than 0`;
    }
    if (fractionalDigits(value) > 2) {
      return `${noun} must have at most 2 decimal places`;
    }
    return true;
  };

export const validateFee = validateCharge("Fee");

export const validateWithholdingTax = validateCharge("Tax");

/** Which validator a charge field runs, keyed exactly as its label is. */
export const CHARGE_VALIDATORS: Record<
  ChargeNoun,
  (value: NumericInput) => string | true
> = {
  fee: validateFee,
  withholdingTax: validateWithholdingTax,
};

// Mirrors the server's dividend and interest command checks, worded identically since
// the message lands on the same field either way.
const taxOverIncomeMessage = (income: TaxedIncome): string =>
  `Tax must not be greater than the ${income} amount`;

// Gross comes from the form values RHF hands every validator, not a prop — a
// prop is captured at the render that registered the field, so it wouldn't see amounts typed afterwards.
export const chargeRule =
  (charge: ChargeNoun, taxedIncome: TaxedIncome = "dividend") =>
  (value: NumericInput, formValues: ChargeFormValues): string | true => {
    const verdict = CHARGE_VALIDATORS[charge](value);
    if (verdict !== true) {
      return verdict;
    }
    if (charge !== "withholdingTax" || typeof value !== "number") {
      return true;
    }
    const gross = formValues.amountEur;
    // An amount that is blank or malformed is the amount field's own rejection
    // to report; comparing against it here would answer with the wrong message.
    if (typeof gross !== "number" || !Number.isFinite(gross)) {
      return true;
    }
    return value <= gross ? true : taxOverIncomeMessage(taxedIncome);
  };

const validateCashbackAmount = validateCharge("Cashback");

// Capped at the amount alone, as the server caps it: a fee is always paid from cash.
export const cashbackRule = (
  value: NumericInput,
  formValues: ChargeFormValues,
): string | true => {
  const verdict = validateCashbackAmount(value);
  if (verdict !== true || typeof value !== "number") {
    return verdict;
  }
  const amount = formValues.amountEur;
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    return true;
  }
  return value <= amount ? true : "Cashback cannot exceed the buy amount";
};

export const cashbackCappedAmountRules = {
  validate: validateAmount,
  deps: ["cashbackEur"],
};

export const cashbackSourceRule = (
  value: CashbackFundingFormValues["cashbackSource"],
  formValues: CashbackFundingFormValues,
): string | true =>
  value !== null || isBlank(formValues.cashbackEur)
    ? true
    : "Pick the account the cashback comes from";
